import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const topBtnStyle = {
  border: "none",
  background: "rgba(255,255,255,0.1)",
  color: "#fff",
  borderRadius: 8,
  padding: "7px 14px",
  fontSize: "0.78rem",
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
};

const navBtnStyle = (enabled) => ({
  border: "none",
  background: "rgba(0,0,0,0.4)",
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.25,
  flexShrink: 0,
});

// Mobile: floats directly on the image's edges instead of its own flex
// column — on a narrow viewport the two 44px buttons + gap + padding ate
// a big chunk of width before the image got any, so a landscape photo
// hit its width constraint and rendered much shorter than the screen.
const overlayNavBtnStyle = (enabled) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  border: "none",
  background: "rgba(0,0,0,0.45)",
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.25,
  zIndex: 2,
});

// Big view (opened by clicking a thumbnail) — filmstrip browser: prev/next
// through the same list the grid is currently showing (`images`, the
// caller's sortedImages), plus a scrollable thumbnail strip, instead of
// having to close and reopen a different thumbnail to see neighbors.
export default function ImageModal({ open, image, images = [], imageUrls, azureBase, onClose, onNavigate, onAnnotate, fetchSasUrlWithCache, onDescriptionChange, onDescriptionBlur }) {
  const retriedRef = useRef(new Set());
  const stripRef = useRef(null);
  const captionRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    const active = stripRef.current?.querySelector('[data-active="true"]');
    // behavior:"smooth" animates the scroll distance regardless of how far
    // it is — on a strip with hundreds of thumbnails still lazy-loading,
    // animating across a big jump (e.g. clicking a thumbnail far away)
    // while images nearby are also decoding is what read as "slow and
    // buggy." An instant jump has nothing to animate, so nothing to stutter.
    active?.scrollIntoView({ block: "nearest", inline: "center", behavior: "auto" });
  }, [open, image?.id]);

  // With 226 images in one subcategory, the grid's own background loader
  // (4-at-a-time) can take a while to reach every image — don't make the
  // currently-open one wait its turn behind that queue.
  useEffect(() => {
    if (!open || !image) return;
    if (!imageUrls?.[image.id]) fetchSasUrlWithCache?.(image);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, image?.id, imageUrls?.[image?.id]]);

  // Reset the "show a spinner" state every time we switch to a different
  // image, so navigating the filmstrip always shows loading feedback again
  // rather than carrying over the previous image's loaded state.
  useEffect(() => {
    setImgLoaded(false);
  }, [image?.id]);

  // Auto-grow the caption textarea, same scrollHeight-based approach as
  // the grid card's resizeTextarea — only one textarea is ever mounted
  // here (whichever image is open), so no need to share refs across cards.
  useLayoutEffect(() => {
    const ta = captionRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = ta.scrollHeight + "px";
  }, [image?.id, image?.metadata?.description]);

  if (!open || !image) return null;

  const isDesktop = windowWidth > 768; // same breakpoint ImageBlock.jsx uses
  const src = imageUrls?.[image.id] || `${azureBase}/${image.azure_blob_name}`;
  const rotation = image.metadata?.rotation || 0;
  const rotated90or270 = rotation % 180 !== 0;

  const index = images.findIndex(i => i.id === image.id);
  const total = images.length;
  const hasPrev = index > 0;
  const hasNext = index !== -1 && index < total - 1;
  const goPrev = () => { if (hasPrev) onNavigate?.(images[index - 1]); };
  const goNext = () => { if (hasNext) onNavigate?.(images[index + 1]); };

  const handleImgError = () => {
    // Could be a blob: URL evicted from the shared memoryCache LRU, or a
    // corrupted raw blob persisted in IndexedDB — force a fresh network
    // fetch rather than re-reading whichever cache just failed.
    if (!retriedRef.current.has(image.id)) {
      retriedRef.current.add(image.id);
      fetchSasUrlWithCache?.(image, true);
    }
  };

  return createPortal(
    <div
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      onClick={onClose}
      style={{ backgroundColor: "#18181a", display: "flex", flexDirection: "column" }}
    >
      <style>{`
        @keyframes ibm-spin { to { transform: rotate(360deg); } }
        .ibm-spinner {
          width: 34px; height: 34px; border-radius: 50%;
          border: 3px solid rgba(255,255,255,0.2);
          border-top-color: #fff;
          animation: ibm-spin 0.8s linear infinite;
        }
        .ibm-caption::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
      {/* Top bar: position + annotate + close */}
      <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", flexShrink: 0 }}>
        <span style={{ fontSize: "0.8rem", color: "#c7c7cb" }}>
          {index !== -1 ? `${index + 1} / ${total}` : ""}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          {onAnnotate && (
            <button type="button" style={topBtnStyle} onClick={onAnnotate} title="Piirrä merkintöjä">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
              Merkinnät
            </button>
          )}
          <button type="button" style={topBtnStyle} onClick={onClose} aria-label="Sulje" title="Sulje">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            Sulje
          </button>
        </div>
      </div>

      {/* Image + prev/next. Desktop keeps dedicated nav-button columns;
          mobile floats the same buttons on the image's edges instead so
          the image gets the width they'd otherwise take (see
          overlayNavBtnStyle above). */}
      <div onClick={e => e.stopPropagation()} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: isDesktop ? 18 : 0, minHeight: 0, padding: isDesktop ? "0 20px" : "0 10px" }}>
        {isDesktop && total > 1 && (
          <button type="button" style={navBtnStyle(hasPrev)} onClick={goPrev} disabled={!hasPrev} aria-label="Edellinen kuva">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
        )}

        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 14, minWidth: 0, maxHeight: "100%",
          ...(isDesktop ? {} : { flex: "1 1 auto", width: "100%", minHeight: 0 }),
        }}>
          <div style={{
            position: "relative", display: "flex", alignItems: "center", justifyContent: "center", maxWidth: "100%",
            maxHeight: isDesktop ? "calc(100vh - 230px)" : "100%",
            ...(isDesktop ? {} : { flex: "1 1 auto", width: "100%", minHeight: 0 }),
          }}>
            <img
              src={src}
              alt={image.metadata?.description || ""}
              draggable={false}
              decoding="async"
              onError={handleImgError}
              onLoad={() => setImgLoaded(true)}
              style={{
                transform: `rotate(${rotation}deg)`,
                // A rotated (portrait-displayed) image's un-rotated layout
                // width becomes its visual height after the transform, so
                // this is sized from vh — but "70vh" alone ignored that the
                // prev/next buttons (and their gap/padding) share this same
                // row: a wide-enough rotated image could grow past the
                // actual leftover space and visually paint over the
                // buttons. min(...) keeps it from ever claiming more width
                // than the row would actually have left for it.
                maxWidth: rotated90or270 ? (isDesktop ? "min(70vh, calc(100vw - 200px))" : "calc(100vh - 190px)") : "100%",
                maxHeight: rotated90or270 ? "100%" : (isDesktop ? "calc(100vh - 230px)" : "100%"),
                display: "block",
                borderRadius: 4,
              }}
            />
            {!imgLoaded && (
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                <div className="ibm-spinner" />
              </div>
            )}

            {!isDesktop && total > 1 && (
              <>
                <button type="button" style={{ ...overlayNavBtnStyle(hasPrev), left: 8 }} onClick={goPrev} disabled={!hasPrev} aria-label="Edellinen kuva">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <button type="button" style={{ ...overlayNavBtnStyle(hasNext), right: 8 }} onClick={goNext} disabled={!hasNext} aria-label="Seuraava kuva">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                </button>
              </>
            )}
          </div>
          <textarea
            ref={captionRef}
            value={image.metadata?.description || ""}
            onChange={e => onDescriptionChange?.(image.id, e.target.value)}
            onBlur={() => onDescriptionBlur?.(image.id)}
            placeholder="Lisää kuvateksti…"
            rows={1}
            className="ib-caption ibm-caption"
            style={{ color: "#e5e5ea", borderBottomColor: "rgba(255,255,255,0.15)", textAlign: "center", maxWidth: 700, width: "100%" }}
          />
        </div>

        {isDesktop && total > 1 && (
          <button type="button" style={navBtnStyle(hasNext)} onClick={goNext} disabled={!hasNext} aria-label="Seuraava kuva">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        )}
      </div>

      {/* Thumbnail filmstrip — horizontally scrollable (can be 100s of
          images in one subcategory), auto-centers the active one. */}
      {total > 1 && (
        <div
          ref={stripRef}
          onClick={e => e.stopPropagation()}
          style={{ display: "flex", gap: 8, overflowX: "auto", padding: "14px 20px 18px", flexShrink: 0 }}
        >
          {images.map(img => (
            <button
              type="button"
              key={img.id}
              data-active={img.id === image.id}
              onClick={() => onNavigate?.(img)}
              aria-label="Avaa kuva"
              aria-current={img.id === image.id}
              style={{
                width: 56,
                height: 56,
                borderRadius: 6,
                flexShrink: 0,
                border: "none",
                padding: 0,
                cursor: "pointer",
                overflow: "hidden",
                background: "#2a2a2d",
                outline: img.id === image.id ? "2px solid #4a9eff" : "none",
                outlineOffset: 2,
              }}
            >
              {imageUrls?.[img.id] && (
                <img
                  src={imageUrls[img.id]}
                  alt=""
                  // A strip this long (hundreds of images, each a full-res
                  // blob being downscaled to 56px) was the actual source of
                  // the scroll lag — loading="lazy" defers decode/raster
                  // until a thumbnail nears the viewport instead of doing
                  // it for all of them up front, and draggable={false}
                  // stops the browser's native image-drag affordance from
                  // fighting the scroll/click gesture here too.
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  // CSS background-image (the previous approach) has no
                  // error event at all — a thumbnail whose blob: URL was
                  // evicted from the shared 150-entry LRU cache just stayed
                  // blank forever, only "fixing itself" once you opened that
                  // exact image (which self-heals via the main <img onError>
                  // below). A real <img> here gets the same self-heal.
                  onError={() => {
                    if (!retriedRef.current.has(img.id)) {
                      retriedRef.current.add(img.id);
                      fetchSasUrlWithCache?.(img, true);
                    }
                  }}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
